/**
 * 商品搜索統一服務
 * 提供多種搜索方案的自動降級機制和統一的數據格式
 */

import { getParameterByName } from './index'

export class ProductSearchService {
  constructor (store) {
    this.store = store
    this.searchStrategies = []
    this.initStrategies()
  }

  /**
   * 初始化搜索策略
   */
  initStrategies () {
    // 策略1：淘寶搜索API (優先)
    this.searchStrategies.push({
      name: 'taobao-search',
      type: 'keyword',
      search: this.searchTaobao.bind(this),
      priority: 1
    })

    // 策略2：代購商品搜索API (備用)
    this.searchStrategies.push({
      name: 'tbdg-search',
      type: 'keyword',
      search: this.searchTbdg.bind(this),
      priority: 2
    })

    // 策略3：圖片搜索
    this.searchStrategies.push({
      name: 'image-search',
      type: 'image',
      search: this.searchByImageImpl.bind(this),
      priority: 1
    })
  }

  /**
   * 關鍵詞搜索 - 自動降級
   * @param {string} keyword 搜索關鍵詞
   * @param {Object} options 搜索選項
   * @returns {Promise<Array>} 統一格式的商品列表
   */
  async searchByKeyword (keyword, options = {}) {
    if (!keyword) {
      throw new Error('搜索關鍵詞不能為空')
    }

    const keywordStrategies = this.searchStrategies
      .filter(s => s.type === 'keyword')
      .sort((a, b) => a.priority - b.priority)

    let lastError = null

    // 依次嘗試各種搜索策略
    for (const strategy of keywordStrategies) {
      try {
        console.log(`嘗試使用 ${strategy.name} 搜索: ${keyword}`)
        const result = await strategy.search(keyword, options)

        if (result && result.length > 0) {
          console.log(`${strategy.name} 搜索成功，返回 ${result.length} 個結果`)
          return result
        }
      } catch (error) {
        console.warn(`${strategy.name} 搜索失敗:`, error.message)
        lastError = error
        continue
      }
    }

    // 所有策略都失敗
    throw lastError || new Error('所有搜索方案都失敗')
  }

  /**
   * 圖片搜索
   * @param {string} imageBase64 圖片base64數據
   * @param {Object} options 搜索選項
   * @returns {Promise<Array>} 統一格式的商品列表
   */
  async searchByImage (imageBase64, options = {}) {
    if (!imageBase64) {
      throw new Error('圖片數據不能為空')
    }

    const strategy = this.searchStrategies.find(s => s.type === 'image')
    if (!strategy) {
      throw new Error('圖片搜索功能不可用')
    }

    return await strategy.search(imageBase64, options)
  }

  /**
   * 補全商品詳情 (懶加載)
   * @param {Object} item 商品信息
   * @returns {Promise<Object>} 補全後的商品信息
   */
  async fillItemDetail (item) {
    // 檢查是否需要補全cid字段
    if (item.itemCid || item.searchSource !== 'tbdg-search') {
      return item
    }

    try {
      console.log(`補全商品詳情: ${item.id}`)
      const res = await this.store.dispatch('tbdg/product/productDetail', {
        itemId: item.id || item.itemid
      })

      if (res.data && res.data.model) {
        const detail = res.data.model
        return {
          ...item,
          itemCid: detail.category_id,
          category: detail.category_id,
          sellerId: detail.sellerUid || detail.shop_id,
          traSellerId: detail.sellerUid || detail.shop_id,
          seller: detail.shop_name,
          // 可以補充其他詳情字段
          ...detail
        }
      }
    } catch (error) {
      console.warn('補全商品詳情失敗:', error.message)
    }

    return item
  }

  /**
   * 根据商品URL获取完整的商品详情
   * @param {string} url 商品URL
   * @returns {Promise<Object>} 包含tpCatalog和item的完整商品信息
   * @throws {Error} 当URL解析失败、商品获取失败或类目未识别时抛出错误
   */
  async fetchItemByUrl (url) {
    if (!url) {
      const error = new Error('商品URL不能為空')
      error.code = 'URL_PARSE_ERROR'
      throw error
    }
    console.log('fetchItemByUrl', url)

    // 1. 尝试解析1688商品URL
    const match1688 = url.match(/offer\/(\d+)\.html/)
    if (match1688) {
      return await this.fetch1688Item(match1688[1])
    }

    // 2. 尝试解析淘宝商品URL
    const taobaoId = getParameterByName('id', url)
    if (taobaoId) {
      return await this.fetchTaobaoItem(taobaoId, url)
    }

    // 3. 无法识别的URL格式
    const error = new Error('無法識別的商品URL格式')
    error.code = 'URL_PARSE_ERROR'
    throw error
  }

  /**
   * 获取淘宝商品详情
   * @param {string} itemid 商品ID
   * @param {string} originalUrl 原始URL（用于错误提示）
   * @returns {Promise<Object>} 商品详情
   */
  async fetchTaobaoItem (itemid, originalUrl = '') {
    try {
      console.log(`獲取淘寶商品詳情: ${itemid}`)

      // 1. 先调用 tbdg/product/productDetail 获取商品详细信息
      const productRes = await this.store.dispatch('tbdg/product/productDetail', {
        itemId: itemid
      })

      if (!productRes.data || !productRes.data.model) {
        const error = new Error('商品信息獲取失敗')
        error.code = 'FETCH_ERROR'
        throw error
      }

      const productDetail = productRes.data.model

      // 2. 构建商品参数
      const params = {
        item: {
          itemid: productDetail.item_id || itemid,
          itemName: productDetail.title,
          itemCid: productDetail.category_id,
          itemCidName: productDetail.category_path || productDetail.category_name,
          itemFinalPrice: productDetail.price,
          itemPic: productDetail.pic_urls[0],
          sellerId: productDetail.seller_id,
          sellerNick: productDetail.shop_name,
        }
      }

      // 3. 调用 tpsp/tpttItem/viewTaobao 获取类目信息
      const catalogRes = await this.store.dispatch('tpsp/tpttItem/viewTaobao', {
        params,
        options: {
          noShowError: true,
          retry: 0,
        }
      })

      if (catalogRes.data && !catalogRes.data.error && catalogRes.data.model) {
        const model = catalogRes.data.model

        // 检查类目是否已识别
        const catalog = model.catalog || model.tpCatalog
        if (!catalog || !catalog.catTwName) {
          const error = new Error(
            `巧巧郎不知道「${originalUrl || itemid}」是什麽，需要查一下資料，請您10分鐘之後再來問我好嗎？這段時間您可以先查詢其他商品喔～`
          )
          error.code = 'UNKNOWN_CATEGORY'
          throw error
        }

        return model
      }

      // 接口返回异常
      const error = new Error(catalogRes.data?.msg || '類目查詢失敗')
      error.code = 'FETCH_ERROR'
      throw error
    } catch (error) {
      // 如果错误已经有code，直接抛出
      if (error.code) {
        throw error
      }

      // 其他错误
      const wrappedError = new Error(error.message || '商品獲取失敗')
      wrappedError.code = 'FETCH_ERROR'
      throw wrappedError
    }
  }

  /**
   * 获取1688商品详情
   * @param {string} offerId 商品ID
   * @returns {Promise<Object>} 商品详情（格式化为与淘宝一致）
   */
  async fetch1688Item (offerId) {
    try {
      console.log(`獲取1688商品詳情: ${offerId}`)
      const res = await this.store.dispatch('magicbuy/dgorder/productDetail', {
        offerId
      })

      if (res.data && !res.data.error && res.data.model) {
        const model = res.data.model

        // 将1688数据格式化为统一格式
        return {
          item: {
            itemid: model.offerId,
            itemName: model.subject,
            itemCid: model.categoryId,
            itemFinalPrice: model.productSkuInfos?.[0]?.price || 50,
            itemPic: model.productImage?.images?.[0] || '',
            traSellerId: model.sellerOpenId,
            traSellerName: model.sellerOpenId,
          },
          // 1688商品可能没有tpCatalog，需要在组件中单独处理
          tpCatalog: null
        }
      }

      // 接口返回错误
      const error = new Error(res.data?.msg || '1688商品獲取失敗')
      error.code = 'FETCH_ERROR'
      throw error
    } catch (error) {
      // 如果错误已经有code，直接抛出
      if (error.code) {
        throw error
      }

      // 其他错误
      const wrappedError = new Error(error.message || '1688商品獲取失敗')
      wrappedError.code = 'FETCH_ERROR'
      throw wrappedError
    }
  }

  /**
   * 淘寶搜索API實現
   */
  async searchTaobao (keyword, options = {}) {
    const params = {
      pageSize: options.pageSize || 12,
      keyword
    }

    const res = await this.store.dispatch('tpsp/tpttItem/searchTaobao', params)

    if (res.data.error) {
      throw new Error(res.data.msg || 'taobao搜索失敗')
    }

    return this.normalizeResults(res.data.list || [], 'taobao-search', options)
  }

  /**
   * 代購商品搜索API實現
   */
  async searchTbdg (keyword, options = {}) {
    const params = {
      page: 1,
      pageSize: options.pageSize || 12,
      keyword
    }

    const res = await this.store.dispatch('tbdg/product/searchKeywordQuery', params)

    if (res.data.error) {
      throw new Error(res.data.msg || 'tbdg搜索失敗')
    }

    // 進行繁體中文轉換
    const converter = window.OpenCC?.Converter({ from: 'cn', to: 'tw' })
    let list = res.data.list || []

    if (converter) {
      list = list.map(item => ({
        ...item,
        title: converter(item.title)
      }))
    }

    return this.normalizeResults(list, 'tbdg-search', options)
  }

  /**
   * 圖片搜索API實現
   */
  async searchByImageImpl (imageBase64, options = {}) {
    const res = await this.store.dispatch('magicbuy/dgorder/searchByImage', {
      imageBase64
    })

    if (res.data.error) {
      throw new Error(res.data.msg || '圖片識別失敗')
    }

    return this.normalizeResults(res.data.list || [], 'image-search', options)
  }

  /**
   * 統一數據格式
   * @param {Array} list 原始數據列表
   * @param {string} source 數據來源
   * @param {Object} options 選項
   * @returns {Array} 統一格式的數據列表
   */
  normalizeResults (list, source, options = {}) {
    const normalized = list.map(item => {
      const normalizedItem = this.normalizeItem(item, source)

      // 應用過濾邏輯
      if (options.filter && typeof options.filter === 'function') {
        return options.filter(normalizedItem) ? normalizedItem : null
      }

      return normalizedItem
    }).filter(Boolean)

    // 限制返回數量
    const limit = options.limit || 12
    return normalized.slice(0, limit)
  }

  /**
   * 統一單個商品數據格式
   */
  normalizeItem (item, source) {
    let normalizedItem = {
      searchSource: source
    }

    switch (source) {
      case 'taobao-search':
        // eslint-disable-next-line no-case-declarations
        let sellerId = item.sellerId || item.traSellerId
        if (!sellerId && item.imageUrls) {
          item.imageUrls.forEach(url => {
            const sellerUidMatch = url.match(/!!(\d+)\./)
            if (sellerUidMatch) {
              const match1 = sellerUidMatch[1]
              // 检查 itemPic 是否有多个相同的 sellerUidMatch[1]，只有重复才设置 sellerUid
              const matchArr = url.match(new RegExp(`${match1}`, 'g'))
              if (matchArr && matchArr.length > 1) {
                sellerId = match1
              }
            }
          })
        }
        // eslint-disable-next-line no-case-declarations
        let itemId = (item.itemid || item.id)
        if (item.itemTbk?.item_id) {
          itemId = item.itemTbk.item_id
        }
        normalizedItem = {
          ...normalizedItem,
          id: itemId,
          itemid: itemId,
          title: item.title || item.itemName,
          itemName: item.title || item.itemName,
          price: item.price || item.itemFinalPrice,
          itemPic: this.getItemPic(item),
          imageUrls: item.imageUrls || (item.itemPic ? [item.itemPic] : []),
          category: item.category || item.itemCid,
          itemCid: item.category || item.itemCid,
          seller: item.seller || item.sellerNick,
          sellerId,
          sellerNick: item.sellerNick || item.traSellerName,
          itemTbk: item.itemTbk,
        }
        break

      case 'tbdg-search':
        normalizedItem = {
          ...normalizedItem,
          id: item.item_id || item.offerId,
          itemid: item.item_id || item.offerId,
          title: item.title,
          itemName: item.title,
          price: item.price,
          itemPic: item.main_image_url,
          imageUrls: item.main_image_url ? [item.main_image_url] : [],
          category: null, // tbdg搜索缺少此字段，需要懶加載
          itemCid: null, // tbdg搜索缺少此字段，需要懶加載
          seller: item.shop_name || item.seller,
          sellerId: item.seller_id,
          sellerNick: item.shop_name
        }
        break

      case 'image-search':
        normalizedItem = {
          ...normalizedItem,
          id: item.item_id,
          itemid: item.item_id,
          title: item.title,
          itemName: item.title,
          price: item.price,
          itemPic: this.getItemPic(item),
          imageUrls: item.pic_urls || [],
          category: item.category_id,
          itemCid: item.category_id,
          seller: item.shop_name,
          sellerId: item.seller_id,
          sellerNick: item.shop_name
        }
        break

      default:
        // 保持原有數據結構
        normalizedItem = { ...item, searchSource: source }
    }

    return normalizedItem
  }

  /**
   * 獲取商品圖片URL
   */
  getItemPic (item) {
    if (item.imageUrls && item.imageUrls.length > 0) {
      return item.imageUrls[0]
    }
    if (item.itemPic) {
      return item.itemPic.replace('_80x80.jpg', '_200x200.jpg')
    }
    if (item.pic_url) {
      return item.pic_url
    }
    if (item.pic_urls && item.pic_urls.length > 0) {
      return item.pic_urls[0]
    }
    if (item.main_image_url) {
      return item.main_image_url
    }
    return ''
  }

  /**
   * 添加新的搜索策略 (擴展接口)
   */
  addStrategy (strategy) {
    this.searchStrategies.push(strategy)
    // 按優先級排序
    this.searchStrategies.sort((a, b) => a.priority - b.priority)
  }
}

// 創建全局實例的工廠函數
export function createProductSearchService (store) {
  return new ProductSearchService(store)
}

// 默認導出服務類
export default ProductSearchService
